import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { loadCharacterCatalog } from "../features/character/catalog/loadCatalog";
import { createExport, parseImport } from "../features/character/domain/export";
import {
  perkRankPreview,
  resolvePerkRankAction,
} from "../features/character/domain/perkRating";
import { CharacterRules } from "../features/character/domain/rules";
import type {
  CatalogRecord,
  CharacterCatalog,
  CharacterDraft,
} from "../features/character/domain/types";
import {
  clearDraft,
  loadDraft,
  saveDraft,
} from "../features/character/storage/draftStore";
import { DicePanel } from "../features/dice/DicePanel";
import { processPortrait } from "../shared/image";
import { t, useLocale } from "../shared/i18n";
import { PipboyProcessedImage } from "../shared/PipboyProcessedImage";
import "./app.css";

type Tab =
  "home" | "origins" | "special" | "skills" | "perks" | "stats" | "dice";
const tabs: Tab[] = ["home", "origins", "special", "skills", "perks", "stats"];

const blankDraft = (): CharacterDraft => ({
  level: 1,
  name: "",
  gender: "",
  backstory: "",
  portrait: "",
  portrait_original: "",
  origin: null,
  origin_traits: [],
  is_robot: false,
  special: { СИЛ: 5, ВСП: 5, ВЫН: 5, ХАР: 5, ИНТ: 5, ЛВК: 5, УДЧ: 5 },
  special_bonus: {},
  special_max: {},
  skills: {},
  perks: {},
  pending_choices: [],
  equipment: [],
  secondary_stats: {},
});

export function App() {
  const { locale, setLocale } = useLocale();
  const [tab, setTab] = useState<Tab>(() =>
    window.location.hash === "#dice" ? "dice" : "home",
  );
  const [catalog, setCatalog] = useState<CharacterCatalog | null>(null);
  const [draft, setDraft] = useState<CharacterDraft>(blankDraft);
  const [storageReady, setStorageReady] = useState(false);
  const [notice, setNotice] = useState<{
    message: string;
    type: "info" | "error";
  } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchIndex, setSearchIndex] = useState(0);
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const [searchMotion, setSearchMotion] = useState(0);
  const [searchActivation, setSearchActivation] = useState<{
    tab: Exclude<Tab, "dice">;
    id: string;
  } | null>(null);
  const [perkFilter, setPerkFilter] = useState<
    "all" | "owned" | "unowned" | "available" | "locked"
  >("all");
  const [perkFilterOpen, setPerkFilterOpen] = useState(false);
  const [utilityOpen, setUtilityOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tooltip, setTooltip] = useState<{
    text: string;
    left: number;
    top: number;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const utilityRef = useRef<HTMLDivElement>(null);
  const utilityToggleRef = useRef<HTMLButtonElement>(null);
  const perkFilterShellRef = useRef<HTMLDivElement>(null);
  const perkFilterTriggerRef = useRef<HTMLButtonElement>(null);
  const subtabRef = useRef<HTMLDivElement>(null);
  const primaryTabRef = useRef<HTMLElement>(null);
  const bottomRailRef = useRef<HTMLElement>(null);
  const tooltipElementRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<number | null>(null);
  const tooltipTargetRef = useRef<HTMLElement | null>(null);
  const tooltipVisibleRef = useRef(false);
  const tooltipDismissedRef = useRef(false);
  const tooltipMouseRef = useRef({ x: 0, y: 0 });
  const noticeTimerRef = useRef<number | null>(null);
  const searchShouldScrollRef = useRef(false);
  const searchShouldRevealRef = useRef(false);
  const rules = useMemo(
    () => (catalog ? new CharacterRules(catalog) : null),
    [catalog],
  );

  useEffect(() => {
    document.body.className = "app-shell pipboy-shell";
    return () => {
      document.body.className = "";
    };
  }, []);

  useEffect(() => {
    const syncHash = () =>
      setTab(window.location.hash === "#dice" ? "dice" : "home");
    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, []);

  useEffect(() => {
    const syncFullscreen = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    const closeFromOutside = (event: MouseEvent) => {
      if (
        utilityOpen &&
        !utilityRef.current?.contains(event.target as Node) &&
        !utilityToggleRef.current?.contains(event.target as Node)
      ) {
        setUtilityOpen(false);
      }
      if (
        perkFilterOpen &&
        !perkFilterShellRef.current?.contains(event.target as Node)
      ) {
        setPerkFilterOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setUtilityOpen(false);
      if (perkFilterOpen) {
        event.preventDefault();
        setPerkFilterOpen(false);
        return;
      }
      if (searchOpen) {
        setSearchOpen(false);
        setSearch("");
        setSearchIndex(0);
      }
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("click", closeFromOutside);
    document.addEventListener("keydown", closeOnEscape);
    syncFullscreen();
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("click", closeFromOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [perkFilterOpen, searchOpen, utilityOpen]);

  useEffect(() => {
    if (!perkFilterOpen) return;
    const frame = window.requestAnimationFrame(() => {
      perkFilterShellRef.current
        ?.querySelector<HTMLButtonElement>(".perks-filter-option.is-active")
        ?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [perkFilterOpen]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const cancelTimer = () => {
      if (tooltipTimerRef.current) window.clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    };
    const hide = (clearTarget = true) => {
      cancelTimer();
      setTooltip(null);
      tooltipVisibleRef.current = false;
      if (clearTarget) {
        tooltipTargetRef.current = null;
        tooltipDismissedRef.current = false;
      }
    };
    const targetFrom = (node: EventTarget | null) =>
      node instanceof Element
        ? node.closest<HTMLElement>("[data-tooltip]")
        : null;
    const validTarget = (target: HTMLElement | null) => {
      const text = target?.dataset.tooltip?.trim();
      return Boolean(
        target &&
        text &&
        !target.matches(":disabled") &&
        target.getAttribute("aria-disabled") !== "true",
      );
    };
    const show = (target: HTMLElement, x: number, y: number) => {
      if (!validTarget(target)) {
        hide();
        return;
      }
      const text = target.dataset.tooltip?.trim() ?? "";
      setTooltip({ text, left: 12, top: 12 });
      tooltipVisibleRef.current = true;
      window.requestAnimationFrame(() => {
        const rect = tooltipElementRef.current?.getBoundingClientRect();
        if (!rect || tooltipTargetRef.current !== target) return;
        const gap = 12;
        const left = Math.min(
          Math.max(gap, x + gap),
          window.innerWidth - rect.width - gap,
        );
        const top =
          y + rect.height + gap < window.innerHeight
            ? y + gap
            : y - rect.height - gap;
        setTooltip({ text, left, top: Math.max(gap, top) });
      });
    };
    const schedule = (target: HTMLElement, x: number, y: number) => {
      if (!validTarget(target)) {
        hide();
        return;
      }
      cancelTimer();
      tooltipMouseRef.current = { x, y };
      setTooltip(null);
      tooltipVisibleRef.current = false;
      tooltipTimerRef.current = window.setTimeout(() => {
        tooltipTimerRef.current = null;
        if (
          tooltipTargetRef.current === target &&
          !tooltipDismissedRef.current
        ) {
          show(target, tooltipMouseRef.current.x, tooltipMouseRef.current.y);
        }
      }, 1000);
    };
    const onMove = (event: MouseEvent) => {
      const target = targetFrom(
        document.elementFromPoint(event.clientX, event.clientY),
      );
      if (target !== tooltipTargetRef.current) {
        hide();
        if (target) {
          tooltipTargetRef.current = target;
          schedule(target, event.clientX, event.clientY);
        }
        return;
      }
      if (!target) {
        hide();
        return;
      }
      const moved =
        event.clientX !== tooltipMouseRef.current.x ||
        event.clientY !== tooltipMouseRef.current.y;
      if (!moved) return;
      tooltipMouseRef.current = { x: event.clientX, y: event.clientY };
      if (tooltipVisibleRef.current) {
        hide(false);
        tooltipDismissedRef.current = true;
      } else if (!tooltipDismissedRef.current) {
        schedule(target, event.clientX, event.clientY);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") hide();
    };
    const hideOnEvent = () => hide();
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", hideOnEvent, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", hideOnEvent, true);
    window.addEventListener("resize", hideOnEvent);
    return () => {
      hide();
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", hideOnEvent, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", hideOnEvent, true);
      window.removeEventListener("resize", hideOnEvent);
    };
  }, []);

  useLayoutEffect(() => {
    const tabBar = subtabRef.current;
    const primaryTabs = primaryTabRef.current;
    const bottomRail = bottomRailRef.current;
    if (!tabBar || !primaryTabs || !bottomRail || tab === "dice") return;
    const sync = () => {
      const subtabs = Array.from(
        tabBar.querySelectorAll<HTMLButtonElement>("[role=tab]"),
      );
      const activeSubtab = subtabs.find(
        (item) => item.getAttribute("aria-selected") === "true",
      );
      const activeTopTab = document.querySelector<HTMLElement>(
        ".pipboy-tabs .pipboy-tab.is-active",
      );
      if (!activeSubtab || !activeTopTab) return;
      const activeIndex = subtabs.indexOf(activeSubtab);
      const gap = Number.parseFloat(getComputedStyle(tabBar).columnGap) || 0;
      const precedingWidth =
        subtabs
          .slice(0, activeIndex)
          .reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) +
        activeIndex * gap;
      const topRect = activeTopTab.getBoundingClientRect();
      const barRect = tabBar.getBoundingClientRect();
      const offset =
        topRect.left +
        topRect.width / 2 -
        barRect.left -
        precedingWidth -
        activeSubtab.getBoundingClientRect().width / 2;
      tabBar.style.setProperty("--pipboy-subtabs-offset", `${offset}px`);
      subtabs.forEach((item, index) => {
        item.style.setProperty(
          "--pipboy-subtab-opacity",
          Math.max(0.2, 1 - Math.abs(index - activeIndex) * 0.18).toFixed(2),
        );
      });
      bottomRail.style.setProperty(
        "--pipboy-bottom-notch-half",
        `${Math.ceil(primaryTabs.getBoundingClientRect().width / 2)}px`,
      );
    };
    const frame = window.requestAnimationFrame(sync);
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(sync) : null;
    observer?.observe(primaryTabs);
    window.addEventListener("resize", sync);
    document.fonts?.ready.then(sync);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [tab]);

  useEffect(
    () => () => {
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    },
    [],
  );

  useLayoutEffect(() => {
    const panel = document.querySelector<HTMLElement>(
      '.wizard-panel.is-active[data-balanced-panel=""]',
    );
    const layout = panel?.querySelector<HTMLElement>("[data-balanced-layout]");
    const detail = panel?.querySelector<HTMLElement>("[data-balanced-detail]");
    if (!panel || !layout || !detail) return;

    const sync = () => {
      if (
        !window.matchMedia("(min-width: 961px)").matches ||
        !panel.clientHeight
      ) {
        layout.style.removeProperty("--balanced-panel-height");
        return;
      }
      const viewport = panel.closest<HTMLElement>(".wizard-grid");
      const viewportStyle = viewport ? getComputedStyle(viewport) : null;
      const verticalPadding = viewportStyle
        ? (Number.parseFloat(viewportStyle.paddingTop) || 0) +
          (Number.parseFloat(viewportStyle.paddingBottom) || 0)
        : 0;
      const availableHeight = Math.ceil(
        Math.max(
          0,
          (viewport?.clientHeight || panel.clientHeight) - verticalPadding,
        ),
      );
      layout.style.setProperty(
        "--balanced-panel-height",
        `${Math.max(availableHeight, Math.ceil(detail.scrollHeight))}px`,
      );
    };
    const schedule = () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(sync));
    };
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(schedule)
        : null;
    observer?.observe(panel);
    observer?.observe(detail);
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [catalog, draft, perkFilter, search, tab]);

  useLayoutEffect(() => {
    const lineCount = (element: Element) => {
      if (!element.textContent?.trim()) return 0;
      const range = document.createRange();
      range.selectNodeContents(element);
      const lines = new Set(
        Array.from(range.getClientRects())
          .filter((rect) => rect.width > 0 && rect.height > 0)
          .map((rect) => Math.round(rect.top)),
      );
      range.detach?.();
      return lines.size;
    };
    const syncDescription = (selector: string) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (!element) return;
      element.classList.toggle("is-single-line", lineCount(element) === 1);
    };
    const syncBlock = (selector: string, itemSelector: string) => {
      const block = document.querySelector<HTMLElement>(selector);
      if (!block) return;
      const items = Array.from(block.querySelectorAll(itemSelector));
      block.classList.toggle(
        "is-single-line-block",
        items.length > 0 && items.every((item) => lineCount(item) === 1),
      );
    };
    const sync = () => {
      syncDescription(".skills-description p");
      syncBlock(".skills-equipment", ".skills-equipment-label, li");
      syncDescription(".perk-description-summary");
    };
    let disposed = false;
    let frame: number | null = null;
    const schedule = () => {
      if (disposed) return;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        sync();
      });
    };
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(schedule)
        : null;
    const mutationObserver =
      typeof MutationObserver === "function"
        ? new MutationObserver(schedule)
        : null;
    document
      .querySelectorAll<HTMLElement>(".skills-description, .perks-description")
      .forEach((element) => {
        resizeObserver?.observe(element);
        mutationObserver?.observe(element, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      });
    document.fonts?.ready.then(schedule);
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      disposed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [catalog, draft, perkFilter, search, tab]);

  useEffect(() => {
    if (!searchOpen || tab === "dice" || !search.trim()) {
      setSearchMatchCount(0);
      setSearchIndex(0);
      document
        .querySelectorAll<HTMLElement>("[data-global-search-item]")
        .forEach((element) => {
          element.hidden = false;
          element.classList.remove("is-search-match", "is-search-active");
        });
      document
        .querySelectorAll<HTMLElement>("[data-global-search-row]")
        .forEach((element) => {
          element.hidden = false;
        });
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const activePanel = document.querySelector(
        `[data-ui-panel="${tab}"].is-active`,
      );
      const query = normalizeSearch(search);
      const matches = Array.from(
        activePanel?.querySelectorAll<HTMLElement>(
          "[data-global-search-item]",
        ) ?? [],
      ).filter((element) =>
        normalizeSearch(
          element.dataset.globalSearchText ?? element.innerText,
        ).includes(query),
      );
      const nextIndex = matches.length ? searchIndex % matches.length : 0;
      const items = Array.from(
        activePanel?.querySelectorAll<HTMLElement>(
          "[data-global-search-item]",
        ) ?? [],
      );
      items.forEach((element) => {
        const isMatch = matches.includes(element);
        element.hidden = !isMatch;
        element.classList.toggle("is-search-match", isMatch);
        element.classList.toggle(
          "is-search-active",
          isMatch && element === matches[nextIndex],
        );
      });
      activePanel
        ?.querySelectorAll<HTMLElement>("[data-global-search-row]")
        .forEach((row) => {
          const rowItems = Array.from(
            row.querySelectorAll<HTMLElement>("[data-global-search-item]"),
          );
          row.hidden =
            rowItems.length > 0 && rowItems.every((item) => item.hidden);
        });
      setSearchMatchCount(matches.length);
      if (nextIndex !== searchIndex) setSearchIndex(nextIndex);
      const shouldReveal = searchShouldRevealRef.current;
      const shouldScroll = searchShouldScrollRef.current;
      searchShouldRevealRef.current = false;
      searchShouldScrollRef.current = false;
      const activeMatch = matches[nextIndex];
      if ((shouldReveal || shouldScroll) && activeMatch) {
        if (tab === "home") {
          activeMatch
            .querySelector<HTMLElement>(
              "input, textarea, select, button:not(:disabled)",
            )
            ?.focus({ preventScroll: true });
        } else if (tab !== "perks") {
          const id = activeMatch.dataset.globalSearchId;
          if (id) setSearchActivation({ tab, id });
        }
        if (shouldScroll) {
          activeMatch.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    catalog,
    draft,
    perkFilter,
    search,
    searchIndex,
    searchMotion,
    searchOpen,
    tab,
  ]);

  useEffect(() => {
    loadDraft()
      .then((stored) => {
        if (stored) setDraft(stored);
        setStorageReady(true);
      })
      .catch(() => {
        showNotice(t(locale, "storageError"), "error");
        setStorageReady(true);
      });
  }, [locale]);

  useEffect(() => {
    if (!storageReady) return;
    const timer = window.setTimeout(() => {
      saveDraft(draft).catch(() =>
        showNotice(t(locale, "storageError"), "error"),
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, locale, storageReady]);

  async function openTab(next: Tab) {
    if (searchOpen && search.trim() && next !== "dice") {
      searchShouldRevealRef.current = true;
    }
    setTab(next);
    setSearchIndex(0);
    if (next === "home" || next === "dice" || catalog) return;
    const loaded = await loadCharacterCatalog();
    const engine = new CharacterRules(loaded);
    setCatalog(loaded);
    setDraft((current) => engine.hydrateDraft(current));
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        const root = document.documentElement;
        try {
          await root.requestFullscreen({ navigationUI: "hide" });
        } catch {
          await root.requestFullscreen();
        }
      }
    } catch {
      // Browser policy may reject fullscreen mode; the UI stays usable.
    }
  }

  function setSearchSurface(open: boolean) {
    setSearchOpen(open);
    if (!open) {
      setSearch("");
      setSearchIndex(0);
      setPerkFilterOpen(false);
    }
  }

  function moveSearch(step: -1 | 1) {
    if (!searchMatchCount) return;
    searchShouldScrollRef.current = true;
    setSearchIndex(
      (index) => (index + step + searchMatchCount) % searchMatchCount,
    );
    setSearchMotion((value) => value + 1);
  }

  function showNotice(message: string, type: "info" | "error" = "info") {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = null;
    setNotice({ message, type });
    if (type === "info") {
      noticeTimerRef.current = window.setTimeout(() => {
        setNotice(null);
        noticeTimerRef.current = null;
      }, 3500);
    }
  }

  function mutate(mutator: (next: CharacterDraft) => boolean | void) {
    if (!rules) return;
    setDraft((current) => {
      const next = rules.hydrateDraft(current);
      if (mutator(next) === false)
        showNotice(t(locale, "actionUnavailable"), "error");
      return next;
    });
  }

  function exportDraft() {
    if (!catalog) return;
    const blob = new Blob(
      [JSON.stringify(createExport(draft, catalog.version), null, 2)],
      { type: "application/json" },
    );
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${draft.name || "fallout-character"}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  async function importDraft(file: File) {
    try {
      const parsed = parseImport(JSON.parse(await file.text()));
      const loaded = catalog ?? (await loadCharacterCatalog());
      setCatalog(loaded);
      setDraft(new CharacterRules(loaded).hydrateDraft(parsed.character));
      showNotice(t(locale, "imported"));
    } catch (error) {
      showNotice(
        error instanceof Error ? error.message : t(locale, "importError"),
        "error",
      );
    }
  }

  const secondary = draft.secondary_stats;
  return (
    <>
      <div className="app-noise" />
      <div
        ref={tooltipElementRef}
        className="pipboy-tooltip pipboy-hud-box"
        role="tooltip"
        hidden={!tooltip}
        style={tooltip ? { left: tooltip.left, top: tooltip.top } : undefined}
      >
        {tooltip?.text}
      </div>
      <header className="topbar pipboy-topbar">
        <button
          ref={utilityToggleRef}
          className="pipboy-utility pipboy-utility--left pipboy-settings-utility"
          type="button"
          onClick={() => setUtilityOpen((open) => !open)}
          aria-expanded={utilityOpen}
          aria-controls="pipboy-utility-menu"
          aria-label={locale === "ru" ? "Открыть функции" : "Open features"}
          data-tooltip={locale === "ru" ? "Открыть функции" : "Open features"}
        >
          ⚙
        </button>
        <div
          ref={utilityRef}
          className="pipboy-utility-menu"
          id="pipboy-utility-menu"
          hidden={!utilityOpen}
        >
          <button
            className="pipboy-menu-action pipboy-language-utility"
            type="button"
            onClick={() => setLocale(locale === "ru" ? "en" : "ru")}
            aria-label={
              locale === "ru"
                ? "Switch interface language to English"
                : "Переключить язык интерфейса на русский"
            }
            data-tooltip={locale === "ru" ? "English" : "Русский язык"}
          >
            {locale === "ru" ? "EN" : "RU"}
          </button>
          <button
            className={`pipboy-menu-action${isFullscreen ? " is-active" : ""}`}
            type="button"
            hidden={!document.fullscreenEnabled}
            onClick={() => {
              void toggleFullscreen();
              setUtilityOpen(false);
            }}
            aria-pressed={isFullscreen}
            aria-label={
              isFullscreen
                ? locale === "ru"
                  ? "Выйти из полноэкранного режима"
                  : "Exit fullscreen mode"
                : locale === "ru"
                  ? "Открыть полноэкранный режим"
                  : "Open fullscreen mode"
            }
            data-tooltip={
              isFullscreen
                ? locale === "ru"
                  ? "Выйти из полноэкранного режима"
                  : "Exit fullscreen mode"
                : locale === "ru"
                  ? "Открыть полноэкранный режим"
                  : "Open fullscreen mode"
            }
          >
            <span
              className="pipboy-fullscreen-utility-icon"
              aria-hidden="true"
            />
          </button>
        </div>
        <nav
          ref={primaryTabRef}
          className="nav-tabs pipboy-tabs"
          aria-label={t(locale, "navigation")}
        >
          <a className="pipboy-tab is-active" href="/" data-pipboy-tab="chrgen">
            CHRGEN
          </a>
          <span className="pipboy-tab is-disabled" aria-disabled="true">
            PLCH1
          </span>
          <span className="pipboy-tab is-disabled" aria-disabled="true">
            DICER
          </span>
          <span className="pipboy-tab is-disabled" aria-disabled="true">
            PLCH2
          </span>
          <span className="pipboy-tab is-disabled" aria-disabled="true">
            PLCH3
          </span>
        </nav>
        <button
          className="pipboy-utility pipboy-utility--right pipboy-search-utility"
          type="button"
          onClick={() => setSearchSurface(!searchOpen)}
          aria-expanded={searchOpen}
          aria-controls="character-search-panel"
          aria-label={
            searchOpen
              ? locale === "ru"
                ? "Закрыть поиск"
                : "Close search"
              : locale === "ru"
                ? "Открыть поиск"
                : "Open search"
          }
          data-tooltip={
            searchOpen
              ? locale === "ru"
                ? "Закрыть поиск"
                : "Close search"
              : locale === "ru"
                ? "Открыть поиск"
                : "Open search"
          }
        >
          <span className="pipboy-search-utility-icon" />
        </button>
        <div
          className={`pipboy-search-overlay${tab === "perks" ? "" : " is-filter-unavailable"}`}
          id="character-search-panel"
          hidden={!searchOpen}
        >
          <input
            ref={searchInputRef}
            className="pipboy-search-overlay__input"
            type="search"
            value={search}
            onChange={(event) => {
              if (tab !== "perks" && tab !== "dice") {
                searchShouldRevealRef.current = true;
              }
              setSearch(event.target.value);
              setSearchIndex(0);
              setSearchActivation(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "F3") {
                event.preventDefault();
                moveSearch(event.shiftKey ? -1 : 1);
              }
            }}
            placeholder={locale === "ru" ? "поиск..." : "search..."}
            aria-label={locale === "ru" ? "Поиск" : "Search"}
          />
          <span className="pipboy-search-overlay__count" aria-live="polite">
            {searchMatchCount
              ? `${searchIndex + 1} / ${searchMatchCount}`
              : "0 / 0"}
          </span>
          <button
            className="pipboy-search-overlay__nav"
            type="button"
            onClick={() => moveSearch(-1)}
            disabled={!searchMatchCount}
            aria-label={
              locale === "ru"
                ? "Предыдущий результат поиска"
                : "Previous search result"
            }
          >
            ▲
          </button>
          <button
            className="pipboy-search-overlay__nav"
            type="button"
            onClick={() => moveSearch(1)}
            disabled={!searchMatchCount}
            aria-label={
              locale === "ru"
                ? "Следующий результат поиска"
                : "Next search result"
            }
          >
            ▼
          </button>
          {tab === "perks" && (
            <div
              ref={perkFilterShellRef}
              className="pipboy-search-overlay__filter"
            >
              <select
                className="perks-filter-native"
                value={perkFilter}
                onChange={(event) =>
                  setPerkFilter(event.target.value as typeof perkFilter)
                }
                aria-label={locale === "ru" ? "Фильтр перков" : "Perk filter"}
              >
                <option value="all">{locale === "ru" ? "Все" : "All"}</option>
                <option value="owned">
                  {locale === "ru" ? "Выбранные" : "Owned"}
                </option>
                <option value="unowned">
                  {locale === "ru" ? "Невыбранные" : "Unowned"}
                </option>
                <option value="available">
                  {locale === "ru" ? "Доступные" : "Available"}
                </option>
                <option value="locked">
                  {locale === "ru" ? "Недоступные" : "Locked"}
                </option>
              </select>
              <button
                ref={perkFilterTriggerRef}
                className="perks-filter-trigger"
                type="button"
                onClick={() => setPerkFilterOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={perkFilterOpen}
                aria-controls="perks-filter-menu"
              >
                <span className="perks-filter-label">
                  {perkFilterLabel(locale, perkFilter)}
                </span>
                <span className="perks-filter-icon" aria-hidden="true" />
              </button>
              <div
                className="perks-filter-menu scroll-area"
                id="perks-filter-menu"
                role="listbox"
                hidden={!perkFilterOpen}
                onKeyDown={(event) => {
                  const options = Array.from(
                    event.currentTarget.querySelectorAll<HTMLButtonElement>(
                      ".perks-filter-option",
                    ),
                  );
                  const index = options.indexOf(
                    document.activeElement as HTMLButtonElement,
                  );
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPerkFilterOpen(false);
                    return;
                  }
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    if (!options.length) return;
                    options[
                      (index +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        options.length) %
                        options.length
                    ]?.focus();
                    return;
                  }
                  if (event.key === "Enter" || event.key === " ") {
                    const active =
                      document.activeElement?.closest<HTMLButtonElement>(
                        ".perks-filter-option",
                      );
                    if (!active) return;
                    event.preventDefault();
                    setPerkFilter(
                      active.dataset.perkFilter as typeof perkFilter,
                    );
                    setPerkFilterOpen(false);
                    window.requestAnimationFrame(() =>
                      perkFilterTriggerRef.current?.focus(),
                    );
                  }
                }}
              >
                {(
                  ["all", "owned", "unowned", "available", "locked"] as const
                ).map((value) => (
                  <button
                    className={`perks-filter-option${perkFilter === value ? " is-active" : ""}`}
                    key={value}
                    type="button"
                    role="option"
                    aria-selected={perkFilter === value}
                    onClick={() => {
                      setPerkFilter(value);
                      setPerkFilterOpen(false);
                      window.requestAnimationFrame(() =>
                        perkFilterTriggerRef.current?.focus(),
                      );
                    }}
                  >
                    {perkFilterLabel(locale, value)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className={`page-wrap${tab === "stats" ? " has-system-hud" : ""}`}>
        {tab === "dice" ? (
          <DicePanel locale={locale} />
        ) : (
          <section
            className="wizard-frame pipboy-frame"
            aria-label="Fallout 2d20 character generator"
          >
            <div
              ref={subtabRef}
              className="wizard-tabs pipboy-subtabs ui-tabs"
              role="tablist"
            >
              {tabs.map((item) => (
                <button
                  key={item}
                  className={`chip${tab === item ? " is-active" : ""}`}
                  onClick={() => void openTab(item)}
                  role="tab"
                  aria-selected={tab === item}
                >
                  {t(locale, `tabs.${item}`)}
                </button>
              ))}
            </div>
            <div className="wizard-content">
              {notice && (
                <div
                  className="wizard-notice"
                  data-notice-type={notice.type}
                  role="status"
                >
                  {notice.message}
                </div>
              )}
              <div className="wizard-grid">
                <Panel id="home" active={tab === "home"}>
                  <HomePanel
                    draft={draft}
                    onChange={(next) =>
                      setDraft(() => (rules ? rules.hydrateDraft(next) : next))
                    }
                    onNotice={(message) => showNotice(message, "error")}
                    locale={locale}
                  />
                </Panel>
                <Panel id="origins" active={tab === "origins"}>
                  {catalog ? (
                    <OriginsPanel
                      catalog={catalog}
                      draft={draft}
                      rules={rules}
                      query={search}
                      locale={locale}
                      searchActivation={
                        searchActivation?.tab === "origins"
                          ? searchActivation.id
                          : null
                      }
                      onNotice={(message) => showNotice(message, "error")}
                      select={(origin, trait) =>
                        mutate((next) =>
                          trait
                            ? rules?.toggleOriginTrait(next, trait)
                            : rules?.setOrigin(next, origin),
                        )
                      }
                      applyPending={(choiceIndex, selection) =>
                        mutate((next) =>
                          rules?.applyPendingChoice(
                            next,
                            selection,
                            choiceIndex,
                          ),
                        )
                      }
                    />
                  ) : (
                    <Loading locale={locale} />
                  )}
                </Panel>
                <Panel id="special" active={tab === "special"}>
                  {catalog ? (
                    <SpecialPanel
                      catalog={catalog}
                      draft={draft}
                      query={search}
                      change={(key, value) =>
                        mutate((next) =>
                          rules?.setSpecial(next, { [key]: value }),
                        )
                      }
                      rules={rules}
                      searchActivation={
                        searchActivation?.tab === "special"
                          ? searchActivation.id
                          : null
                      }
                    />
                  ) : (
                    <Loading locale={locale} />
                  )}
                </Panel>
                <Panel id="skills" active={tab === "skills"}>
                  {catalog ? (
                    <SkillsPanel
                      catalog={catalog}
                      draft={draft}
                      query={search}
                      locale={locale}
                      rules={rules}
                      searchActivation={
                        searchActivation?.tab === "skills"
                          ? searchActivation.id
                          : null
                      }
                      toggle={(name) =>
                        mutate((next) =>
                          rules?.setMainSkill(
                            next,
                            name,
                            !next.skills[name]?.is_main,
                          ),
                        )
                      }
                      rank={(name, amount) =>
                        mutate((next) =>
                          rules?.changeSkillRank(next, name, amount),
                        )
                      }
                    />
                  ) : (
                    <Loading locale={locale} />
                  )}
                </Panel>
                <Panel id="perks" active={tab === "perks"}>
                  {catalog ? (
                    <PerksPanel
                      catalog={catalog}
                      draft={draft}
                      query={search}
                      filter={perkFilter}
                      rules={rules}
                      locale={locale}
                      change={(name, rank) =>
                        mutate((next) => rules?.setPerkRank(next, name, rank))
                      }
                    />
                  ) : (
                    <Loading locale={locale} />
                  )}
                </Panel>
                <Panel id="stats" active={tab === "stats"}>
                  {catalog ? (
                    <StatsPanel
                      draft={draft}
                      onExport={exportDraft}
                      onImport={importDraft}
                      onReset={async () => {
                        await clearDraft();
                        setDraft(
                          (rules?.createDraft() ??
                            blankDraft()) as CharacterDraft,
                        );
                        showNotice(t(locale, "reset"));
                      }}
                    />
                  ) : (
                    <Loading locale={locale} />
                  )}
                </Panel>
              </div>
            </div>
          </section>
        )}
      </main>
      {tab !== "dice" && (
        <>
          <footer
            ref={bottomRailRef}
            className="pipboy-bottom-rail"
            aria-hidden="true"
            hidden={tab === "stats"}
          />
          {tab === "stats" && (
            <footer
              className="system-hud"
              aria-label="Character system information"
            >
              <div className="system-hud__item pipboy-hud-box">
                <span>
                  {t(locale, "level")} {draft.level}
                </span>
              </div>
              <div className="system-hud__item pipboy-hud-box">
                <span>
                  {t(locale, "origin")} {draft.origin ?? "—"}
                </span>
              </div>
              <div className="system-hud__item pipboy-hud-box">
                <span>
                  {t(locale, "health")} {secondary.max_hp ?? "—"}
                </span>
              </div>
            </footer>
          )}
        </>
      )}
    </>
  );
}

function Panel({
  id,
  active,
  children,
}: {
  id: Tab;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`wizard-panel scroll-area${active ? " is-active" : ""}`}
      data-ui-panel={id}
      data-balanced-panel={id === "skills" || id === "perks" ? "" : undefined}
    >
      {children}
    </section>
  );
}
function Loading({ locale }: { locale: string }) {
  return <p className="microcopy">{t(locale, "loading")}</p>;
}

function HomePanel({
  draft,
  onChange,
  onNotice,
  locale,
}: {
  draft: CharacterDraft;
  onChange: (draft: CharacterDraft) => void;
  onNotice: (message: string) => void;
  locale: string;
}) {
  const [original, setOriginal] = useState(false);
  const [status, setStatus] = useState<"ready" | "editing" | "saved">("ready");
  const statusTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (statusTimer.current) window.clearTimeout(statusTimer.current);
    },
    [],
  );
  function update(next: CharacterDraft) {
    if (statusTimer.current) window.clearTimeout(statusTimer.current);
    setStatus("editing");
    onChange(next);
    statusTimer.current = window.setTimeout(() => setStatus("saved"), 500);
  }
  async function selectPortrait(file: File) {
    if (!file.type.startsWith("image/")) {
      onNotice(
        locale === "ru"
          ? "Выберите файл изображения."
          : "Select an image file.",
      );
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onNotice(
        locale === "ru"
          ? "Размер портрета не должен превышать 5 МБ."
          : "Portrait size must not exceed 5 MB.",
      );
      return;
    }
    try {
      const portrait = await processPortrait(file);
      update({
        ...draft,
        portrait: portrait.processed,
        portrait_original: portrait.original,
      });
    } catch (error) {
      onNotice(
        error instanceof Error
          ? error.message
          : locale === "ru"
            ? "Не удалось обработать портрет."
            : "Unable to process portrait.",
      );
    }
  }
  const image =
    original && draft.portrait_original
      ? draft.portrait_original
      : draft.portrait;
  return (
    <div className="home-identity-panel">
      <div className="home-identity-header">
        <span className="eyebrow">
          {locale === "ru" ? "ПРОФИЛЬ ПЕРСОНАЖА" : "CHARACTER PROFILE"}
        </span>
        <span className="home-identity-status">
          {locale === "ru"
            ? status === "editing"
              ? "РЕДАКТИРОВАНИЕ"
              : status === "saved"
                ? "СОХРАНЕНО"
                : "ГОТОВ"
            : status === "editing"
              ? "EDITING"
              : status === "saved"
                ? "SAVED"
                : "READY"}
        </span>
      </div>
      <div className="home-identity-grid">
        <div
          className="home-portrait-field"
          data-global-search-item
          data-global-search-text={t(locale, "portrait")}
        >
          <div className="home-portrait-frame">
            {image ? (
              <img src={image} alt={t(locale, "portrait")} />
            ) : (
              <span>{t(locale, "noPortrait")}</span>
            )}
            {draft.portrait_original && (
              <button
                className="portrait-toggle"
                type="button"
                onClick={() => setOriginal((value) => !value)}
                aria-label={
                  original
                    ? locale === "ru"
                      ? "Показать обработанный портрет"
                      : "Show processed portrait"
                    : locale === "ru"
                      ? "Показать оригинальный портрет"
                      : "Show original portrait"
                }
                data-tooltip={
                  original
                    ? locale === "ru"
                      ? "Показать обработанный портрет"
                      : "Show processed portrait"
                    : locale === "ru"
                      ? "Показать оригинальный портрет"
                      : "Show original portrait"
                }
              >
                <svg
                  className="portrait-eye-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
              </button>
            )}
          </div>
          <label className="cta home-upload-button">
            {locale === "ru" ? "ЗАГРУЗИТЬ ПОРТРЕТ" : "UPLOAD PORTRAIT"}
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void selectPortrait(file);
              }}
            />
          </label>
        </div>
        <label
          className="field"
          data-global-search-item
          data-global-search-text={`${t(locale, "level")} ${draft.level}`}
        >
          <span>{t(locale, "level")}</span>
          <input
            type="number"
            min="1"
            max="21"
            value={draft.level}
            onChange={(event) =>
              update({
                ...draft,
                level: Math.max(
                  1,
                  Math.min(21, Number(event.target.value) || 1),
                ),
              })
            }
          />
        </label>
        <label
          className="field"
          data-global-search-item
          data-global-search-text={`${t(locale, "name")} ${draft.name}`}
        >
          <span>{t(locale, "name")}</span>
          <input
            value={draft.name}
            maxLength={120}
            onChange={(event) => update({ ...draft, name: event.target.value })}
          />
        </label>
        <label
          className="field"
          data-global-search-item
          data-global-search-text={`${t(locale, "gender")} ${draft.gender}`}
        >
          <span>{t(locale, "gender")}</span>
          <select
            value={draft.gender}
            onChange={(event) =>
              update({
                ...draft,
                gender: event.target.value as CharacterDraft["gender"],
              })
            }
          >
            <option value="">—</option>
            <option value="Male">{t(locale, "male")}</option>
            <option value="Female">{t(locale, "female")}</option>
            <option value="Other">{t(locale, "other")}</option>
          </select>
        </label>
        <label
          className="field home-backstory-field"
          data-global-search-item
          data-global-search-text={`${t(locale, "backstory")} ${draft.backstory}`}
        >
          <span>{t(locale, "backstory")}</span>
          <textarea
            value={draft.backstory}
            maxLength={4000}
            onChange={(event) =>
              update({ ...draft, backstory: event.target.value })
            }
          />
        </label>
      </div>
    </div>
  );
}

function OriginsPanel({
  catalog,
  draft,
  rules,
  query,
  locale,
  searchActivation,
  onNotice,
  select,
  applyPending,
}: {
  catalog: CharacterCatalog;
  draft: CharacterDraft;
  rules: CharacterRules | null;
  query: string;
  locale: string;
  searchActivation: string | null;
  onNotice: (message: string) => void;
  select: (origin: string, trait?: string) => void;
  applyPending: (choiceIndex: number, selection: string[]) => void;
}) {
  const [open, setOpen] = useState<string | null>(draft.origin);
  const [pendingSelection, setPendingSelection] = useState<
    Record<number, string[]>
  >({});
  const [selectedEquipmentSet, setSelectedEquipmentSet] = useState(0);
  const [columnCount, setColumnCount] = useState(() =>
    window.innerWidth <= 640 ? 1 : window.innerWidth <= 960 ? 2 : 4,
  );
  useEffect(() => {
    const update = () =>
      setColumnCount(
        window.innerWidth <= 640 ? 1 : window.innerWidth <= 960 ? 2 : 4,
      );
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const shown = catalog.origins;
  useEffect(() => {
    if (searchActivation) setOpen(searchActivation);
  }, [searchActivation]);
  useEffect(() => {
    const normalized = normalizeSearch(query);
    if (!normalized) return;
    const match = shown.find((item) => searchIndex(item).includes(normalized));
    if (match) setOpen(match.name);
  }, [query, shown]);
  const origin =
    shown.find((item) => item.name === open) ??
    catalog.origins.find((item) => item.name === open);
  const traits = (origin?.traits as CatalogRecord[] | undefined) ?? [];
  const traitLayout = (() => {
    const traitCount = traits.length;
    if (traitCount < 3) {
      return { mode: "row", columns: Math.max(1, traitCount), rows: 1 };
    }
    if (traitCount >= 10) {
      return {
        mode: "column",
        columns: Math.max(1, Math.ceil(traitCount / 5)),
        rows: Math.min(5, traitCount),
      };
    }
    const columns = Math.min(3, Math.ceil(traitCount / 2));
    return {
      mode: "row",
      columns,
      rows: Math.max(1, Math.ceil(traitCount / columns)),
    };
  })();
  const detailGridWidths =
    traits.length >= 10 && traitLayout.columns >= 2
      ? { copy: "0.75fr", choices: "1.25fr" }
      : { copy: "1.2fr", choices: "0.8fr" };
  const rows = Array.from(
    { length: Math.ceil(shown.length / columnCount) },
    (_, index) => shown.slice(index * columnCount, (index + 1) * columnCount),
  );
  return (
    <div className="stack-list origin-grid">
      {rows.map((row, rowIndex) => {
        const openRow = Boolean(
          origin && row.some((item) => item.name === origin.name),
        );
        return (
          <div
            className={`origin-grid__row${openRow ? " is-open" : ""}`}
            key={rowIndex}
            data-global-search-row
          >
            <div className="origin-grid__tiles">
              {row.map((item) => {
                const traitCount = Array.isArray(item.traits)
                  ? item.traits.length
                  : 0;
                return (
                  <button
                    className={`origin-grid__tile${draft.origin === item.name ? " is-selected" : ""}${open === item.name ? " is-open" : ""}${openRow ? " is-detail-adjacent" : ""}`}
                    key={item.id}
                    data-global-search-item
                    data-global-search-text={searchIndex(item)}
                    data-global-search-id={item.name}
                    onClick={() => {
                      select(item.name);
                      setOpen((current) =>
                        current === item.name ? null : item.name,
                      );
                    }}
                    aria-expanded={open === item.name}
                  >
                    <span className="origin-grid__caption">
                      <strong>{highlightSearch(item.name, query)}</strong>
                      <span>{traitCount} ЧЕРТ</span>
                    </span>
                    <pre className="origin-grid__illustration">
                      {String(item.illustration ?? "")}
                    </pre>
                  </button>
                );
              })}
            </div>
            {origin && openRow && (
              <section className="origin-grid__detail">
                <div
                  className="origin-grid__detail-grid"
                  style={
                    {
                      "--origin-copy-fr": detailGridWidths.copy,
                      "--origin-choices-fr": detailGridWidths.choices,
                    } as CSSProperties
                  }
                >
                  <div className="origin-grid__copy">
                    <span className="origin-grid__label">ОПИСАНИЕ</span>
                    <p>{highlightSearch(origin.description, query)}</p>
                  </div>
                  <div className="origin-grid__choices">
                    <div
                      className={`origin-grid__trait-list is-${traitLayout.mode}-flow`}
                      style={
                        {
                          "--origin-trait-columns": traitLayout.columns,
                          "--origin-trait-rows": traitLayout.rows,
                        } as CSSProperties
                      }
                    >
                      {traits.map((trait) => {
                        const selected = draft.origin_traits.includes(
                          trait.name,
                        );
                        const candidate = selected
                          ? draft.origin_traits.filter(
                              (name) => name !== trait.name,
                            )
                          : [...draft.origin_traits, trait.name];
                        const disabled =
                          !selected &&
                          !rules?.validTraitSelection?.(origin, candidate);
                        return (
                          <div
                            className={`origin-grid__trait-card${selected ? " is-active" : ""}${disabled ? " is-disabled" : ""}`}
                            key={trait.id}
                          >
                            <div className="origin-grid__trait-heading">
                              <button
                                className={`origin-grid__choice-dot${selected ? " is-active" : ""}`}
                                onClick={() => select(origin.name, trait.name)}
                                aria-pressed={selected}
                                aria-disabled={disabled}
                                disabled={disabled}
                              />
                              <strong>
                                {highlightSearch(trait.name, query)}
                              </strong>
                            </div>
                            <p>{highlightSearch(trait.description, query)}</p>
                          </div>
                        );
                      })}
                      {draft.pending_choices.map((choice, choiceIndex) => {
                        const options = Array.isArray(choice.options)
                          ? choice.options
                          : choice.type === "special_bonus"
                            ? catalog.special.map((attribute) =>
                                String(attribute.key),
                              )
                            : catalog.skills.map((skill) => skill.name);
                        const selected = pendingSelection[choiceIndex] ?? [];
                        return (
                          <section
                            className="origin-grid__trait-card"
                            key={`${choice.source}-${choiceIndex}`}
                          >
                            <strong>{choice.source}</strong>
                            <p>
                              {choice.type === "special_bonus"
                                ? `Выберите ${choice.count} характеристик SPECIAL.`
                                : `Выберите ${choice.count} навыков.`}
                            </p>
                            <div className="chip-row" role="group">
                              {options.map((option) => {
                                const active = selected.includes(option);
                                const blocked =
                                  !active && selected.length >= choice.count;
                                return (
                                  <button
                                    className={`chip${active ? " is-active" : ""}`}
                                    type="button"
                                    key={option}
                                    aria-pressed={active}
                                    onClick={() => {
                                      if (blocked) {
                                        onNotice(
                                          locale === "ru"
                                            ? `Выберите не более ${choice.count} вариантов.`
                                            : `Choose no more than ${choice.count} options.`,
                                        );
                                        return;
                                      }
                                      setPendingSelection((current) => ({
                                        ...current,
                                        [choiceIndex]: active
                                          ? selected.filter(
                                              (value) => value !== option,
                                            )
                                          : [...selected, option],
                                      }));
                                    }}
                                  >
                                    {option}
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              className="chip"
                              type="button"
                              disabled={selected.length !== choice.count}
                              onClick={() => {
                                applyPending(choiceIndex, selected);
                                setPendingSelection((current) => {
                                  const next = { ...current };
                                  delete next[choiceIndex];
                                  return next;
                                });
                              }}
                            >
                              Применить выбор
                            </button>
                          </section>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="origin-grid__equipment-section">
                  <span className="origin-grid__label origin-grid__traits-label">
                    {locale === "ru" ? "НАБОР СНАРЯЖЕНИЯ" : "EQUIPMENT SET"}
                  </span>
                  <div className="origin-grid__equipment-list">
                    {[1, 2, 3].map((number, index) => (
                      <button
                        className={`origin-grid__trait origin-grid__equipment${selectedEquipmentSet === index ? " is-active" : ""}`}
                        type="button"
                        key={number}
                        aria-pressed={selectedEquipmentSet === index}
                        onClick={() => setSelectedEquipmentSet(index)}
                      >
                        <strong>
                          {locale === "ru"
                            ? `Набор снаряжения #${number}`
                            : `Equipment set #${number}`}
                        </strong>
                        <span>
                          {locale === "ru"
                            ? "Заглушка стартового набора"
                            : "Starting equipment placeholder"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SpecialPanel({
  catalog,
  draft,
  query,
  change,
  rules,
  searchActivation,
}: {
  catalog: CharacterCatalog;
  draft: CharacterDraft;
  query: string;
  change: (key: string, value: number) => void;
  rules: CharacterRules | null;
  searchActivation: string | null;
}) {
  const [selected, setSelected] = useState(() =>
    String(catalog.special[0]?.key ?? ""),
  );
  const attributes = catalog.special;
  useEffect(() => {
    if (searchActivation) setSelected(searchActivation);
  }, [searchActivation]);
  useEffect(() => {
    const normalized = normalizeSearch(query);
    if (!normalized) return;
    const match = attributes.find((item) =>
      searchIndex(item).includes(normalized),
    );
    if (match) setSelected(String(match.key));
  }, [attributes, query]);
  const current =
    catalog.special.find((item) => String(item.key) === selected) ??
    attributes[0];
  return (
    <div className="special-layout">
      <div className="special-sidebar">
        <div className="special-list" role="listbox">
          {attributes.map((attribute) => {
            const key = String(attribute.key);
            const value = draft.special[key] ?? 5;
            const minimum = Number(rules?.getSpecialMinimum?.(draft, key) ?? 1);
            const maximum = Math.min(10, Number(draft.special_max[key] ?? 10));
            const canDecrease = value > minimum;
            const canIncrease =
              value < maximum &&
              (rules?.specialPointsAvailable?.(draft) ?? 0) > 0;
            return (
              <div
                className={`special-item${selected === key ? " is-active" : ""}`}
                key={attribute.id}
                data-global-search-item
                data-global-search-text={searchIndex(attribute)}
                data-global-search-id={key}
                role="option"
                aria-selected={selected === key}
              >
                <button
                  className="special-select"
                  onClick={() => setSelected(key)}
                  data-special-select={key}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowRight" && canIncrease) {
                      event.preventDefault();
                      change(key, value + 1);
                      return;
                    }
                    if (event.key === "ArrowLeft" && canDecrease) {
                      event.preventDefault();
                      change(key, value - 1);
                      return;
                    }
                    if (
                      !["ArrowDown", "ArrowUp", "Home", "End"].includes(
                        event.key,
                      )
                    ) {
                      return;
                    }
                    event.preventDefault();
                    const buttons = Array.from(
                      event.currentTarget
                        .closest(".special-list")
                        ?.querySelectorAll<HTMLButtonElement>(
                          "[data-special-select]",
                        ) ?? [],
                    );
                    const index = buttons.indexOf(event.currentTarget);
                    const nextIndex =
                      event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? buttons.length - 1
                          : (index +
                              (event.key === "ArrowDown" ? 1 : -1) +
                              buttons.length) %
                            buttons.length;
                    const next = buttons[nextIndex];
                    if (!next) return;
                    setSelected(next.dataset.specialSelect ?? key);
                    next.focus();
                  }}
                >
                  <span className="special-letter">
                    {String(attribute.letter ?? "")}
                  </span>
                  <span className="special-name">
                    {highlightSearch(attribute.name, query)}
                  </span>
                </button>
                <div className="special-value-control">
                  <button
                    className="special-adjust"
                    disabled={!canDecrease}
                    onClick={() => change(key, value - 1)}
                  >
                    ◀
                  </button>
                  <input
                    className="special-value"
                    type="number"
                    min={minimum}
                    max={maximum}
                    value={value}
                    aria-label={String(attribute.name ?? key)}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      if (Number.isFinite(next)) change(key, next);
                    }}
                  />
                  <button
                    className="special-adjust"
                    disabled={!canIncrease}
                    onClick={() => change(key, value + 1)}
                  >
                    ▶
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={`special-image-placeholder${
          current && typeof current.image === "string" ? " has-image" : ""
        }`}
      >
        {current && typeof current.image === "string" ? (
          <PipboyProcessedImage
            className="special-processed-image"
            source={`/${current.image}`}
            alt={String(current.image_alt ?? current.name)}
            placeholder="SPECIAL"
          />
        ) : (
          <span>SPECIAL</span>
        )}
      </div>
      <div className="special-bottom">
        <div className="special-points">
          <div className="special-points-content">
            <strong className="special-points-value">
              {rules?.specialPointsAvailable?.(draft) ?? "—"}
            </strong>
            <span>ДОСТУПНЫЕ ОЧКИ</span>
          </div>
        </div>
        <article className="special-description">
          <p>{highlightSearch(current?.description, query)}</p>
        </article>
      </div>
    </div>
  );
}

function SkillsPanel({
  catalog,
  draft,
  query,
  locale,
  rules,
  toggle,
  rank,
  searchActivation,
}: {
  catalog: CharacterCatalog;
  draft: CharacterDraft;
  query: string;
  locale: string;
  rules: CharacterRules | null;
  toggle: (name: string) => void;
  rank: (name: string, amount: 1 | -1) => void;
  searchActivation: string | null;
}) {
  const [selected, setSelected] = useState(() => catalog.skills[0]?.name ?? "");
  const skills = catalog.skills;
  useEffect(() => {
    if (searchActivation) setSelected(searchActivation);
  }, [searchActivation]);
  useEffect(() => {
    const normalized = normalizeSearch(query);
    if (!normalized) return;
    const match = skills.find((item) =>
      searchIndex({
        skill: item,
        equipment: skillEquipment(catalog, item),
      }).includes(normalized),
    );
    if (match) setSelected(match.name);
  }, [catalog, query, skills]);
  const current =
    catalog.skills.find((skill) => skill.name === selected) ?? skills[0];
  const equipment = current ? skillEquipment(catalog, current) : [];
  const selectedMain = rules?.selectedMainSkills?.(draft) ?? 0;
  const available = rules?.availableSkillRanks?.(draft) ?? 0;
  const distributed = Object.values(draft.skills).reduce(
    (sum, skill) => sum + Math.max(0, skill.invested),
    0,
  );
  const totalRanks = distributed + available;
  return (
    <div className="skills-layout" data-balanced-layout>
      <div className="skills-sidebar" data-balanced-sidebar>
        <div className="skills-list" role="listbox" aria-label="Навыки">
          {skills.map((skill) => {
            const value = draft.skills[skill.name] ?? {
              base: 0,
              invested: 0,
              is_main: false,
            };
            const total = value.base + value.invested;
            const canSetMain = Boolean(
              rules?.canSetMainSkill?.(draft, skill.name, !value.is_main),
            );
            const canIncrease = Boolean(
              (rules?.availableSkillRanks?.(draft) ?? 0) > 0 &&
              total < (rules?.effectiveSkillMax?.(draft, skill.name) ?? 4),
            );
            const canDecrease = value.invested > 0;
            const attributeCheck =
              locale === "ru"
                ? `ИСПОЛЬЗУЕТСЯ ДЛЯ ПРОВЕРКИ ${skill.attr ?? ""} (${skill.attr_ru ?? ""})`
                : `USED FOR CHECKS: ${skill.attr ?? skill.attr_ru ?? ""}`;
            return (
              <div
                className={`skill-item${selected === skill.name ? " is-active" : ""}`}
                key={skill.id}
                data-global-search-item
                data-global-search-text={searchIndex({
                  skill,
                  equipment: skillEquipment(catalog, skill),
                })}
                data-global-search-id={skill.name}
                role="option"
                aria-selected={selected === skill.name}
              >
                <button
                  className={`skill-main-toggle${value.is_main ? " is-main" : ""}`}
                  onClick={() => toggle(skill.name)}
                  aria-pressed={value.is_main}
                  aria-label={
                    locale === "ru"
                      ? `Отметить основной навык: ${skill.name}`
                      : `Mark main skill: ${skill.name}`
                  }
                  disabled={!canSetMain}
                />
                <button
                  className="skill-select"
                  onClick={() => setSelected(skill.name)}
                  onDoubleClick={() => toggle(skill.name)}
                  aria-label={
                    locale === "ru"
                      ? `Показать описание: ${skill.name}`
                      : `Show description: ${skill.name}`
                  }
                >
                  <span className="skill-name">
                    {highlightSearch(skill.name, query)}
                  </span>
                  <span
                    className="skill-attribute"
                    data-tooltip={attributeCheck}
                  >
                    {highlightSearch(attributeCheck, query)}
                  </span>
                </button>
                <div className="skill-value-control">
                  <button
                    className="skill-adjust"
                    disabled={!canDecrease}
                    onClick={() => rank(skill.name, -1)}
                  >
                    ◀
                  </button>
                  <strong className="skill-value">{total}</strong>
                  <button
                    className="skill-adjust"
                    disabled={!canIncrease}
                    onClick={() => rank(skill.name, 1)}
                  >
                    ▶
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="skills-detail" data-balanced-detail>
        <div
          className={`skills-image-placeholder${
            current && typeof current.image === "string" ? " has-image" : ""
          }`}
        >
          {current && typeof current.image === "string" ? (
            <PipboyProcessedImage
              className="skills-processed-image"
              source={`/${current.image}`}
              alt={String(current.image_alt ?? current.name)}
              placeholder="НАВЫКИ"
            />
          ) : (
            <span>НАВЫКИ</span>
          )}
        </div>
        <article className="skills-description">
          <p>
            {highlightSearch(
              current?.desc ?? current?.description ?? "",
              query,
            )}
          </p>
          <div className="skills-equipment">
            <span className="skills-equipment-label">СТАРТОВОЕ СНАРЯЖЕНИЕ</span>
            <ul>
              {equipment.map((item) => (
                <li key={item.id}>
                  <strong>{highlightSearch(item.name, query)}</strong>
                  <span>{highlightSearch(item.description, query)}</span>
                </li>
              ))}
            </ul>
          </div>
        </article>
        <div className="skills-points">
          <div className="skills-selection-status">
            <div className="skills-metric">
              <strong className="skills-metric-value">{selectedMain}/3</strong>
              <span className="skills-metric-label">
                ОТМЕЧЕНО ОСНОВНЫХ НАВЫКОВ
              </span>
            </div>
            <div className="skills-metric">
              <strong className="skills-metric-value">
                {distributed}/{totalRanks}
              </strong>
              <span className="skills-metric-label">
                РАСПРЕДЕЛЕНО ОЧКОВ НАВЫКОВ
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PerksPanel({
  catalog,
  draft,
  query,
  filter,
  rules,
  locale,
  change,
}: {
  catalog: CharacterCatalog;
  draft: CharacterDraft;
  query: string;
  filter: "all" | "owned" | "unowned" | "available" | "locked";
  rules: CharacterRules | null;
  locale: string;
  change: (name: string, rank: number) => void;
}) {
  const [selected, setSelected] = useState(() => catalog.perks[0]?.name ?? "");
  const [preview, setPreview] = useState<{
    name: string;
    rank: number | null;
  } | null>(null);
  const perks = catalog.perks
    .map((perk) => {
      const rank = draft.perks[perk.name] ?? 0;
      const available = Boolean(
        rules?.meetsRequirements?.(draft, perk.requirements ?? {}) &&
        (rules?.freePerkSlots?.(draft) ?? 0) > 0,
      );
      const status: "owned" | "available" | "locked" = rank
        ? "owned"
        : available
          ? "available"
          : "locked";
      return { perk, status };
    })
    .filter(({ perk, status }) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "owned" && status === "owned") ||
        (filter === "unowned" && status !== "owned") ||
        (filter === "available" && status === "available") ||
        (filter === "locked" && status === "locked");
      const searchable = `${searchIndex(perk)} ${status} owned rank ${draft.perks[perk.name] ?? 0}`;
      return (
        matchesFilter &&
        (!query.trim() ||
          normalizeSearch(searchable).includes(normalizeSearch(query)))
      );
    });
  const visiblePerkNames = perks.map(({ perk }) => perk.name).join("\u0000");
  useEffect(() => {
    if (perks.length && !perks.some(({ perk }) => perk.name === selected)) {
      setSelected(perks[0].perk.name);
    }
  }, [perks, selected, visiblePerkNames]);
  const current =
    perks.find(({ perk }) => perk.name === selected)?.perk ?? perks[0]?.perk;
  const rank = current ? (draft.perks[current.name] ?? 0) : 0;
  const max = Number(current?.ranks ?? 1);
  const used = Object.values(draft.perks).reduce(
    (sum, item) => sum + Number(item),
    0,
  );
  const totalSlots = draft.level + Number(draft.bonus_perk_slots ?? 0);
  return (
    <div className="perks-layout" data-balanced-layout>
      <div className="perks-sidebar" data-balanced-sidebar>
        <div className="perks-list" role="listbox" aria-label="Перки">
          {perks.length === 0 ? (
            <p className="empty-state">
              {locale === "ru"
                ? "Перки не соответствуют поиску."
                : "No perks match the search."}
            </p>
          ) : (
            perks.map(({ perk, status }) => {
              const value = draft.perks[perk.name] ?? 0;
              const perkMax = Number(perk.ranks ?? 1);
              const freeSlots = rules?.freePerkSlots?.(draft) ?? 0;
              const ranks = perkRankPreview({
                currentRank: value,
                previewRank: preview?.name === perk.name ? preview.rank : null,
                maxRanks: perkMax,
              });
              return (
                <article
                  className={`perk-item perk-${status}${selected === perk.name ? " is-active" : ""}${value ? " perk-owned" : ""}`}
                  key={perk.id}
                  data-global-search-item
                  data-global-search-text={`${searchIndex(perk)} ${status} owned rank ${value}`}
                  role="option"
                  aria-selected={selected === perk.name}
                >
                  <div className="perk-head">
                    <button
                      className="perk-select"
                      onClick={() => setSelected(perk.name)}
                    >
                      <strong>{highlightSearch(perk.name, query)}</strong>
                      <span className="perk-requirements">
                        {highlightSearch(
                          `${formatPerkRequirements(perk, locale)} (${perkStatusLabel(status, locale)})`,
                          query,
                        )}
                      </span>
                    </button>
                    <div
                      className="perk-row-ranks"
                      role="group"
                      aria-label={`${perk.name}: ${value}/${perkMax}`}
                      onMouseLeave={() => setPreview(null)}
                    >
                      {ranks.map((rankState) => {
                        const action = resolvePerkRankAction({
                          currentRank: value,
                          targetRank: rankState.rank,
                          maxRanks: perkMax,
                          freeSlots,
                          canIncrease: status !== "locked",
                        });
                        const previewRank =
                          value > 0 && rankState.rank === value
                            ? null
                            : action.nextRank;
                        return (
                          <button
                            className={`perk-rank-star${rankState.displayFilled ? " is-filled" : ""}${rankState.addition ? " is-hover-preview" : ""}${rankState.removal ? " is-removal-preview" : ""}`}
                            key={rankState.rank}
                            disabled={!action.enabled}
                            aria-label={`${perk.name}: ${action.mode} ${action.nextRank}`}
                            aria-pressed={rankState.filled}
                            onFocus={() =>
                              setPreview({ name: perk.name, rank: previewRank })
                            }
                            onBlur={() => setPreview(null)}
                            onMouseEnter={() =>
                              setPreview({ name: perk.name, rank: previewRank })
                            }
                            onKeyDown={(event) => {
                              if (
                                ![
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Home",
                                  "End",
                                ].includes(event.key)
                              ) {
                                return;
                              }
                              const stars = Array.from(
                                event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                                  ".perk-rank-star:not(:disabled)",
                                ) ?? [],
                              );
                              if (!stars.length) return;
                              event.preventDefault();
                              const index = stars.indexOf(event.currentTarget);
                              const nextIndex =
                                event.key === "Home"
                                  ? 0
                                  : event.key === "End"
                                    ? stars.length - 1
                                    : (index +
                                        (event.key === "ArrowRight" ? 1 : -1) +
                                        stars.length) %
                                      stars.length;
                              stars[nextIndex]?.focus();
                            }}
                            onClick={() => change(perk.name, action.nextRank)}
                          >
                            {rankState.displayFilled ? "★" : "☆"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
      <div className="perks-detail" data-balanced-detail>
        <div
          className={`perks-image-placeholder${
            current && typeof current.image === "string" ? " has-image" : ""
          }`}
        >
          {current && typeof current.image === "string" ? (
            <PipboyProcessedImage
              className="perks-processed-image"
              source={`/${current.image}`}
              alt={String(current.image_alt ?? current.name)}
              placeholder="ПЕРК"
            />
          ) : (
            <span>ПЕРК</span>
          )}
        </div>
        <article className="perks-description">
          <div className="perks-detail-ranks">
            {Array.from({ length: max }, (_, index) => (
              <span
                className={`perk-detail-rank-star${index < rank ? " is-filled" : ""}`}
                key={index}
              >
                {index < rank ? "★" : "☆"}
              </span>
            ))}
          </div>
          <p className="perk-description-summary">
            {highlightSearch(current?.description, query)}
          </p>
          {current && rankEffects(current).length > 0 && (
            <span className="perk-rank-effects">
              {rankEffects(current).map(([level, effect]) => (
                <span className="perk-rank-effect" key={level}>
                  <strong className="perk-rank-effect-label">
                    РАНГ {level}:
                  </strong>{" "}
                  {highlightSearch(effect, query)}
                </span>
              ))}
            </span>
          )}
        </article>
        <div className="perks-slots">
          <span className="perks-slots-prefix">ЯЧЕЙКИ ПЕРКОВ:</span>
          <strong
            className="perks-slots-box"
            aria-label={
              locale === "ru"
                ? `Использовано ${used} из ${totalSlots} ячеек перков`
                : `${used} of ${totalSlots} perk slots used`
            }
          >
            {used}/{totalSlots}
          </strong>
        </div>
      </div>
    </div>
  );
}

function StatsPanel({
  draft,
  onExport,
  onImport,
  onReset,
}: {
  draft: CharacterDraft;
  onExport: () => void;
  onImport: (file: File) => void;
  onReset: () => void;
}) {
  return (
    <>
      <div
        className="chip-row summary-actions"
        data-global-search-item
        data-global-search-text="export import reset"
      >
        <button className="chip" onClick={onExport}>
          ЭКСПОРТ JSON
        </button>
        <label className="chip summary-import">
          ИМПОРТ JSON
          <input
            type="file"
            accept="application/json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onImport(file);
            }}
          />
        </label>
        <button className="chip" onClick={onReset}>
          СБРОСИТЬ ПЕРСОНАЖА
        </button>
      </div>
      <pre
        className="summary-box"
        data-global-search-item
        data-global-search-text={JSON.stringify(draft)}
      >
        {JSON.stringify(
          {
            ...draft.secondary_stats,
            origin: draft.origin,
            traits: draft.origin_traits,
            perks: draft.perks,
            equipment: draft.equipment,
          },
          null,
          2,
        )}
      </pre>
    </>
  );
}

function normalizeSearch(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

const searchIgnoredKeys = new Set(["id", "image", "image_alt", "illustration"]);

function searchIndex(value: unknown) {
  const values: string[] = [];
  const visited = new WeakSet<object>();
  const collect = (current: unknown): void => {
    if (
      typeof current === "string" ||
      typeof current === "number" ||
      typeof current === "boolean"
    ) {
      values.push(String(current));
      return;
    }
    if (!current || typeof current !== "object" || visited.has(current)) return;
    visited.add(current);
    if (Array.isArray(current)) {
      current.forEach(collect);
      return;
    }
    Object.entries(current).forEach(([key, item]) => {
      if (!searchIgnoredKeys.has(key)) collect(item);
    });
  };
  collect(value);
  return normalizeSearch(values.join(" "));
}

function highlightSearch(value: unknown, query: string): ReactNode {
  const text = String(value ?? "");
  const term = query.trim();
  if (!term) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const expression = new RegExp(escaped, "ig");
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(expression)) {
    const index = match.index ?? 0;
    nodes.push(text.slice(lastIndex, index));
    nodes.push(
      <mark className="search-highlight" key={`${index}-${match[0]}`}>
        {match[0]}
      </mark>,
    );
    lastIndex = index + match[0].length;
  }
  nodes.push(text.slice(lastIndex));
  return nodes;
}

function perkFilterLabel(
  locale: string,
  filter: "all" | "owned" | "unowned" | "available" | "locked",
) {
  const values =
    locale === "ru"
      ? {
          all: "Все",
          owned: "Выбранные",
          unowned: "Невыбранные",
          available: "Доступные",
          locked: "Недоступные",
        }
      : {
          all: "All",
          owned: "Owned",
          unowned: "Unowned",
          available: "Available",
          locked: "Locked",
        };
  return values[filter];
}

function perkStatusLabel(
  status: "owned" | "available" | "locked",
  locale: string,
) {
  return locale === "ru"
    ? { owned: "ВЫБРАН", available: "ДОСТУПЕН", locked: "НЕДОСТУПЕН" }[status]
    : { owned: "OWNED", available: "AVAILABLE", locked: "LOCKED" }[status];
}

function formatPerkRequirements(perk: CatalogRecord, locale: string) {
  const requirements = perk.requirements;
  const none = locale === "ru" ? "БЕЗ ТРЕБОВАНИЙ" : "NO REQUIREMENTS";
  if (!requirements || typeof requirements !== "object") return none;
  const values = Object.entries(requirements as Record<string, unknown>);
  if (!values.length) return none;
  return values
    .map(([key, value]) => {
      if (key === "robot") return locale === "ru" ? "РОБОТ" : "ROBOT";
      if (key === "not_robot")
        return locale === "ru" ? "НЕ РОБОТ" : "NOT ROBOT";
      return `${key.toUpperCase()} ≥ ${value}`;
    })
    .join(" · ");
}

function rankEffects(perk: CatalogRecord): [string, string][] {
  const effects = perk.rank_effects;
  if (!effects || typeof effects !== "object") return [];
  return Object.entries(effects as Record<string, unknown>)
    .map(([rank, effect]) => [rank, String(effect)] as [string, string])
    .sort(([left], [right]) => Number(left) - Number(right));
}

function skillEquipment(
  catalog: CharacterCatalog,
  skill: CatalogRecord,
): CatalogRecord[] {
  const sets = catalog.equipment_sets[String(skill.equipment_set)] ?? [];
  return sets.flatMap((group, groupIndex) => {
    const items = (Array.isArray(group.items) ? group.items : [])
      .map((id) => catalog.equipment.find((item) => item.id === id))
      .filter((item): item is CatalogRecord => Boolean(item));
    if (!group.choice || !items.length) return items;
    return [
      {
        id: `choice:${skill.id}:${groupIndex}`,
        name: items.map((item) => item.name).join(" или "),
        description: "Выберите один вариант",
      },
    ];
  });
}
